import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from './AuthContext';
import { useCommunityContext } from './CommunityContext';
import { dataFlowLog, summarizeCollection } from '../utils/dataFlowLogger';
import { buildPreviewFeed, mergeRecentCommentsWithPost, upsertPost } from '../utils/homeFeed';
import useQueryScope from '../queries/useQueryScope';
import { readOfflineSnapshot, writeOfflineSnapshot, ownsQueryScope } from '../queries/queryOwnership';
import queryKeys from '../queries/queryKeys';
import useNewsImagePreload from '../hooks/useNewsImagePreload';

const defaultNewsContext = {
  previewFeed: [],
  setPreviewFeed: () => {},
  newsFeed: [],
  setNewsFeed: () => {},
  upsertNewsPost: () => {},
  hasFetchedFullNews: false,
  setHasFetchedFullNews: () => {},
  setRecentComments: () => {},
  recentComments: false,
};

const NewsContext = createContext(defaultNewsContext);

export const useNewsContext = () => useContext(NewsContext);

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

export const NewsProvider = ({ children }) => {
  const { user, token } = useAuthContext() || {};
  const { community } = useCommunityContext() || {};
  const queryClient = useQueryClient();
  const userScope = user?._id || user?.id || user?.userId || 'guest';
  const communityScope = community?._id || community?.id || 'no-community';
  const { scope, isCurrent } = useQueryScope();
  const ownsScope = useCallback(() => userScope !== 'guest' && isCurrent(), [isCurrent, userScope]);
  const previewQueryKey = useMemo(() => queryKeys.news.preview(scope), [scope]);
  const fullFeedQueryKey = useMemo(() => queryKeys.news.fullFeed(scope), [scope]);
  const recentCommentsQueryKey = useMemo(() => queryKeys.news.recentComments(scope), [scope]);
  const hasFetchedFullNewsQueryKey = useMemo(() => queryKeys.news.hasFetchedFullNews(scope), [scope]);
  const initialPreviewFeed = useMemo(() => readOfflineSnapshot('previewFeed', scope, []), [scope]);
  const initialRecentComments = useMemo(() => readOfflineSnapshot('recentComments', scope, false), [scope]);

  const previewQuery = useQuery({
    queryKey: previewQueryKey,
    queryFn: async () => [],
    enabled: false,
    initialData: initialPreviewFeed,
  });
  const fullFeedQuery = useQuery({
    queryKey: fullFeedQueryKey,
    queryFn: async () => [],
    enabled: false,
    initialData: [],
  });
  const recentCommentsQuery = useQuery({
    queryKey: recentCommentsQueryKey,
    queryFn: async () => false,
    enabled: false,
    initialData: initialRecentComments,
  });
  const hasFetchedFullNewsQuery = useQuery({
    queryKey: hasFetchedFullNewsQueryKey,
    queryFn: async () => false,
    enabled: false,
    initialData: false,
  });

  const previewFeed = normalizeArray(previewQuery.data);
  const newsFeed = normalizeArray(fullFeedQuery.data);
  const recentComments = recentCommentsQuery.data ?? false;
  const hasFetchedFullNews = Boolean(hasFetchedFullNewsQuery.data);
  useNewsImagePreload({
    userId: userScope === 'guest' ? null : userScope,
    communityId: communityScope === 'no-community' ? null : communityScope,
    token,
    posts: newsFeed.length ? newsFeed : previewFeed,
  });

  const setPreviewFeed = useCallback((nextFeed, options = {}) => {
    if (options.scope ? !ownsQueryScope(options.scope) : !ownsScope()) return;
    const target = options.scope || scope;
    const targetKey = queryKeys.news.preview(target);
    const current = normalizeArray(queryClient.getQueryData(targetKey));
    const resolved = typeof nextFeed === 'function' ? nextFeed(current) : nextFeed;
    const normalized = normalizeArray(resolved);
    queryClient.setQueryData(targetKey, normalized);
    writeOfflineSnapshot('previewFeed', target, normalized);
  }, [ownsScope, scope, queryClient]);

  const setRecentComments = useCallback((nextComments, options = {}) => {
    if (options.scope ? !ownsQueryScope(options.scope) : !ownsScope()) return;
    const target = options.scope || scope;
    const targetKey = queryKeys.news.recentComments(target);
    const current = queryClient.getQueryData(targetKey) ?? false;
    const resolved = typeof nextComments === 'function' ? nextComments(current) : nextComments;
    queryClient.setQueryData(targetKey, resolved);
    writeOfflineSnapshot('recentComments', target, resolved);
  }, [ownsScope, queryClient, scope]);

  const setNewsFeed = useCallback((nextFeed, options = {}) => {
    if (options.scope ? !ownsQueryScope(options.scope) : !ownsScope()) return;
    const targetKey = queryKeys.news.fullFeed(options.scope || scope);
    const current = normalizeArray(queryClient.getQueryData(targetKey));
    const resolved = typeof nextFeed === 'function' ? nextFeed(current) : nextFeed;
    queryClient.setQueryData(targetKey, normalizeArray(resolved));
  }, [scope, ownsScope, queryClient]);

  const setHasFetchedFullNews = useCallback((nextValue, options = {}) => {
    if (options.scope ? !ownsQueryScope(options.scope) : !ownsScope()) return;
    const targetKey = queryKeys.news.hasFetchedFullNews(options.scope || scope);
    const current = Boolean(queryClient.getQueryData(targetKey));
    const resolved = typeof nextValue === 'function' ? nextValue(current) : nextValue;
    queryClient.setQueryData(targetKey, Boolean(resolved));
  }, [scope, ownsScope, queryClient]);

  const upsertNewsPost = useCallback((incomingPost, { prependIfMissing = false } = {}) => {
    if (!incomingPost || typeof incomingPost !== 'object') return;
    setNewsFeed((prev) => upsertPost(prev, incomingPost, { prependIfMissing }));
    setPreviewFeed((prev) => buildPreviewFeed(upsertPost(prev, incomingPost, { prependIfMissing })));
    setRecentComments((prev) => mergeRecentCommentsWithPost(prev, incomingPost));
  }, [setNewsFeed, setPreviewFeed, setRecentComments]);

  useEffect(() => {
    dataFlowLog('news-context.updated', {
      previewFeed: summarizeCollection(previewFeed),
      newsFeed: summarizeCollection(newsFeed),
      recentComments: summarizeCollection(recentComments),
      hasFetchedFullNews,
    });
  }, [hasFetchedFullNews, newsFeed, previewFeed, recentComments]);

  const value = useMemo(() => ({
    previewFeed,
    setPreviewFeed,
    newsFeed,
    setNewsFeed,
    upsertNewsPost,
    hasFetchedFullNews,
    setHasFetchedFullNews,
    setRecentComments,
    recentComments,
  }), [
    hasFetchedFullNews,
    newsFeed,
    previewFeed,
    recentComments,
    setHasFetchedFullNews,
    setNewsFeed,
    setPreviewFeed,
    setRecentComments,
    upsertNewsPost,
  ]);

  return (
    <NewsContext.Provider value={value}>
      {children}
    </NewsContext.Provider>
  );
};
